import { initialsOf } from '@/app/accounts';
import type { Account } from '@/api/types';

/**
 * Where the unified mail view lives, and how a row says which mailbox it is
 * from (ADR 0056 §5, §7, §8).
 *
 * Small and dependency-light on purpose: the router, the shell's sidebar, the
 * handheld drawer and the mail view itself all need the same two strings, and
 * a second spelling of either is a link that silently goes nowhere.
 */

/**
 * A label's view (plan/17 §5): `/mail/label/<account>/<label id>`, with an
 * opened message one segment further. The account is in the path because a
 * label belongs to one mailbox, as a message does (ADR 0012); a static
 * segment, so it outranks `mail/:folderId/:messageId`.
 */
export const LABEL_SEGMENT = 'label';

export function labelViewPath(accountId: string, labelId: string): string {
  return `/mail/${LABEL_SEGMENT}/${encodeURIComponent(accountId)}/${encodeURIComponent(labelId)}`;
}

export function labelMessagePath(accountId: string, labelId: string, messageId: string): string {
  return `${labelViewPath(accountId, labelId)}/${encodeURIComponent(messageId)}`;
}

/** The label id a pathname is showing, and whose mailbox it is; undefined elsewhere. */
export function labelRouteOf(pathname: string): { accountId: string; labelId: string } | undefined {
  const match = /^\/mail\/label\/([^/]+)\/([^/]+)/.exec(pathname);
  if (!match) return undefined;
  try {
    return { accountId: decodeURIComponent(match[1]!), labelId: decodeURIComponent(match[2]!) };
  } catch {
    return undefined;
  }
}

/** The folder-list entry's route. A static segment, so it outranks `mail/:folderId`. */
export const UNIFIED_SEGMENT = 'unified';
export const UNIFIED_PATH = `/mail/${UNIFIED_SEGMENT}`;

/** What the entry, the toolbar and the search scope all call it. */
export const UNIFIED_LABEL = 'All inboxes';

/**
 * The path of one message inside the unified list.
 *
 * The account is a segment because a message id is unique only within an
 * account (ADR 0012) — the same reason `/message/:accountId/:messageId` and
 * `/compose/:accountId` already carry one.
 */
export function unifiedMessagePath(accountId: string, messageId: string): string {
  return `${UNIFIED_PATH}/${encodeURIComponent(accountId)}/${encodeURIComponent(messageId)}`;
}

/** A row's mailbox chip: short text first, colour second. */
export interface AccountChip {
  /** What the chip prints — short enough for a phone row. */
  label: string;
  /** The full address, as the chip's accessible name. */
  title: string;
  /** The account's existing swatch, used only as a tint behind the text. */
  color: string;
}

/**
 * Chips for a set of accounts, keyed by account id.
 *
 * The label is the mailbox's local part when that is distinctive, and
 * `local@host` when two mailboxes share one — "claude-api" twice would be worse
 * than useless in exactly the self-attached case the feature is tested with.
 * Colour is never the only signal: the text carries the meaning and the tint is
 * the shortcut (ADR 0056 §8).
 */
export function accountChips(
  accounts: readonly Account[],
  colorOf: (account: Account, index: number) => string,
): Map<string, AccountChip> {
  const localParts = accounts.map((account) => account.email.replace(/@.*$/, ''));
  const chips = new Map<string, AccountChip>();
  accounts.forEach((account, index) => {
    const local = localParts[index]!;
    const ambiguous = localParts.filter((part) => part === local).length > 1;
    chips.set(account.id, {
      label: ambiguous ? account.email : local || initialsOf(account.email),
      title: account.displayName ? `${account.displayName} — ${account.email}` : account.email,
      color: colorOf(account, index),
    });
  });
  return chips;
}
